import React, { useState, useEffect, useCallback } from 'react'
import { motion } from 'framer-motion'
import {
  Settings,
  User,
  Lock,
  Bell,
  CreditCard,
  Globe,
  Smartphone,
  Mail,
  Shield,
  Eye,
  EyeOff,
  Camera,
  Check,
  ChevronRight,
  Download,
  Trash2,
  AlertCircle,
  Key,
  Fingerprint,
  MapPin,
  Calendar,
  DollarSign,
  RefreshCw,
  Loader2
} from 'lucide-react'
import { SettingsAPI, CardsAPI, AuthAPI } from '../services/api'

const settingsSections = [
  {
    id: 'profile',
    title: 'Profile',
    icon: User,
    description: 'Manage your personal information'
  },
  {
    id: 'security',
    title: 'Security',
    icon: Lock,
    description: 'Password, 2FA, and security settings'
  },
  {
    id: 'notifications',
    title: 'Notifications',
    icon: Bell,
    description: 'Email, SMS, and push notifications'
  },
  {
    id: 'cards',
    title: 'Cards & Payments',
    icon: CreditCard,
    description: 'Manage your cards and payment methods'
  },
  {
    id: 'preferences',
    title: 'Preferences',
    icon: Globe,
    description: 'Language, currency, and display options'
  },
  {
    id: 'privacy',
    title: 'Privacy',
    icon: Shield,
    description: 'Data sharing and privacy controls'
  }
]

export default function SettingsPage() {
  const [activeSection, setActiveSection] = useState('profile')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState('')
  const [sessions, setSessions] = useState([])
  const [cards, setCards] = useState([])
  const [passwordModal, setPasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' })
  const [settings, setSettings] = useState({
    // Profile
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',

    // Security
    twoFactorEnabled: false,
    biometricsEnabled: false,

    // Notifications
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    transactionAlerts: true,
    securityAlerts: true,
    marketingEmails: false,

    // Preferences
    language: 'en',
    currency: 'USD',
    timezone: 'America/New_York',
    theme: 'light'
  })

  // Fetch settings on mount
  const fetchSettings = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [settingsRes, sessionsRes, cardsRes] = await Promise.all([
        SettingsAPI.get(),
        SettingsAPI.getSessions(),
        CardsAPI.getAll()
      ])

      if (settingsRes.success) {
        const data = settingsRes.data
        setSettings({
          firstName: data.first_name || '',
          lastName: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || '',
          city: data.city || '',
          state: data.state || '',
          zipCode: data.zip_code || '',
          twoFactorEnabled: data.two_factor_enabled || false,
          biometricsEnabled: data.biometrics_enabled || false,
          emailNotifications: data.email_notifications ?? true,
          smsNotifications: data.sms_notifications ?? true,
          pushNotifications: data.push_notifications ?? true,
          transactionAlerts: data.transaction_alerts ?? true,
          securityAlerts: data.security_alerts ?? true,
          marketingEmails: data.marketing_emails ?? false,
          language: data.language || 'en',
          currency: data.currency || 'USD',
          timezone: data.timezone || 'America/New_York',
          theme: data.theme || 'light'
        })
      }

      if (sessionsRes.success) {
        setSessions(sessionsRes.data || [])
      }

      if (cardsRes.success) {
        setCards(cardsRes.data || [])
      }
    } catch (err) {
      console.error('Error fetching settings:', err)
      setError('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  const showSuccess = (message) => {
    setSuccessMessage(message)
    setTimeout(() => setSuccessMessage(''), 3000)
  }

  const saveProfile = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await SettingsAPI.updateProfile({
        first_name: settings.firstName,
        last_name: settings.lastName,
        email: settings.email,
        phone: settings.phone
      })
      if (res.success) {
        showSuccess('Profile updated successfully')
      } else {
        setError(res.message || 'Failed to update profile')
      }
    } catch (err) {
      setError(err.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  const saveAddress = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await SettingsAPI.updateAddress({
        address: settings.address,
        city: settings.city,
        state: settings.state,
        zip_code: settings.zipCode
      })
      if (res.success) {
        showSuccess('Address updated successfully')
      } else {
        setError(res.message || 'Failed to update address')
      }
    } catch (err) {
      setError(err.message || 'Failed to update address')
    } finally {
      setSaving(false)
    }
  }

  const saveNotifications = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await SettingsAPI.update({
        email_notifications: settings.emailNotifications,
        sms_notifications: settings.smsNotifications,
        push_notifications: settings.pushNotifications,
        transaction_alerts: settings.transactionAlerts,
        security_alerts: settings.securityAlerts,
        marketing_emails: settings.marketingEmails
      })
      if (res.success) {
        showSuccess('Notification preferences saved')
      } else {
        setError(res.message || 'Failed to save notifications')
      }
    } catch (err) {
      setError(err.message || 'Failed to save notifications')
    } finally {
      setSaving(false)
    }
  }

  const savePreferences = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await SettingsAPI.update({
        language: settings.language,
        currency: settings.currency,
        timezone: settings.timezone,
        theme: settings.theme
      })
      if (res.success) {
        showSuccess('Preferences saved successfully')
      } else {
        setError(res.message || 'Failed to save preferences')
      }
    } catch (err) {
      setError(err.message || 'Failed to save preferences')
    } finally {
      setSaving(false)
    }
  }

  const toggle2FA = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await SettingsAPI.toggle2FA(!settings.twoFactorEnabled)
      if (res.success) {
        updateSetting('twoFactorEnabled', !settings.twoFactorEnabled)
        showSuccess(settings.twoFactorEnabled ? '2FA disabled' : '2FA enabled')
      } else {
        setError(res.message || 'Failed to toggle 2FA')
      }
    } catch (err) {
      setError(err.message || 'Failed to toggle 2FA')
    } finally {
      setSaving(false)
    }
  }

  const revokeSession = async (sessionId) => {
    try {
      const res = await SettingsAPI.revokeSession(sessionId)
      if (res.success) {
        setSessions(prev => prev.filter(s => s.id !== sessionId))
        showSuccess('Session revoked')
      } else {
        setError(res.message || 'Failed to revoke session')
      }
    } catch (err) {
      setError(err.message || 'Failed to revoke session')
    }
  }

  const changePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      setError('Passwords do not match')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await AuthAPI.changePassword(passwordData.current, passwordData.new)
      if (res.success) {
        setPasswordModal(false)
        setPasswordData({ current: '', new: '', confirm: '' })
        showSuccess('Password changed successfully')
      } else {
        setError(res.message || 'Failed to change password')
      }
    } catch (err) {
      setError(err.message || 'Failed to change password')
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-500">Loading settings...</p>
        </div>
      </div>
    )
  }

  const renderProfileSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-6">Profile Information</h2>

        {/* Profile Photo */}
        <div className="mb-8">
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
                {settings.firstName[0]}{settings.lastName[0]}
              </div>
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white hover:bg-slate-800 transition-colors">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">
                {settings.firstName} {settings.lastName}
              </h3>
              <p className="text-xs text-slate-500 mb-3">{settings.email}</p>
              <button className="text-sm font-medium text-slate-900 hover:text-slate-700">
                Change Photo
              </button>
            </div>
          </div>
        </div>

        {/* Personal Info */}
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">First Name</label>
            <input
              type="text"
              value={settings.firstName}
              onChange={(e) => updateSetting('firstName', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Last Name</label>
            <input
              type="text"
              value={settings.lastName}
              onChange={(e) => updateSetting('lastName', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Email Address</label>
            <input
              type="email"
              value={settings.email}
              onChange={(e) => updateSetting('email', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Phone Number</label>
            <input
              type="tel"
              value={settings.phone}
              onChange={(e) => updateSetting('phone', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="pt-6 border-t border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900 mb-4">Mailing Address</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-slate-700 mb-2">Street Address</label>
            <input
              type="text"
              value={settings.address}
              onChange={(e) => updateSetting('address', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">City</label>
            <input
              type="text"
              value={settings.city}
              onChange={(e) => updateSetting('city', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">State</label>
              <input
                type="text"
                value={settings.state}
                onChange={(e) => updateSetting('state', e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-2">ZIP Code</label>
              <input
                type="text"
                value={settings.zipCode}
                onChange={(e) => updateSetting('zipCode', e.target.value)}
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-6">
        <button
          onClick={saveAddress}
          disabled={saving}
          className="px-6 py-2.5 border border-slate-200 hover:border-slate-900 text-slate-900 rounded-lg transition-all text-sm font-medium disabled:opacity-50"
        >
          Save Address
        </button>
        <button
          onClick={saveProfile}
          disabled={saving}
          className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 animate-spin" />}
          Save Profile
        </button>
      </div>
    </div>
  )

  const renderSecuritySection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-6">Security Settings</h2>

        {/* Password */}
        <div className="p-5 bg-white border border-slate-200 rounded-xl mb-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                <Key className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Password</h3>
                <p className="text-xs text-slate-500">Change your account password</p>
              </div>
            </div>
            <button
              onClick={() => setPasswordModal(true)}
              className="text-sm font-medium text-slate-900 hover:text-slate-700"
            >
              Change
            </button>
          </div>

          {/* Password Change Modal */}
          {passwordModal && (
            <div className="mt-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Current Password</label>
                  <input
                    type="password"
                    value={passwordData.current}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">New Password</label>
                  <input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Confirm New Password</label>
                  <input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                    className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <button
                    onClick={() => {
                      setPasswordModal(false)
                      setPasswordData({ current: '', new: '', confirm: '' })
                    }}
                    className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={changePassword}
                    disabled={saving || !passwordData.current || !passwordData.new || !passwordData.confirm}
                    className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center gap-2"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Two-Factor Authentication */}
        <div className="p-5 bg-white border border-slate-200 rounded-xl mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Two-Factor Authentication</h3>
                <p className="text-xs text-slate-500 mb-3">
                  Add an extra layer of security to your account
                </p>
                {settings.twoFactorEnabled && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                    <Check className="w-3 h-3" />
                    Enabled
                  </span>
                )}
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.twoFactorEnabled}
                onChange={toggle2FA}
                disabled={saving}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
            </label>
          </div>
        </div>

        {/* Biometric Authentication */}
        <div className="p-5 bg-white border border-slate-200 rounded-xl mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Fingerprint className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-1">Biometric Authentication</h3>
                <p className="text-xs text-slate-500 mb-3">
                  Use fingerprint or Face ID to log in
                </p>
                {settings.biometricsEnabled && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600">
                    <Check className="w-3 h-3" />
                    Enabled
                  </span>
                )}
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.biometricsEnabled}
                onChange={(e) => updateSetting('biometricsEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
            </label>
          </div>
        </div>

        {/* Active Sessions */}
        <div className="p-5 bg-white border border-slate-200 rounded-xl">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Active Sessions</h3>
              <p className="text-xs text-slate-500">Manage devices where you're logged in</p>
            </div>
            <button
              onClick={fetchSettings}
              className="text-sm font-medium text-slate-900 hover:text-slate-700 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              Refresh
            </button>
          </div>

          <div className="space-y-3 mt-4">
            {sessions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No active sessions</p>
            ) : (
              sessions.map((session) => (
                <div key={session.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {session.device_type === 'mobile' ? (
                      <Smartphone className="w-5 h-5 text-slate-600" />
                    ) : (
                      <Globe className="w-5 h-5 text-slate-600" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {session.device_name || session.browser || 'Unknown Device'}
                      </p>
                      <p className="text-xs text-slate-500">
                        {session.location || 'Unknown location'} • {session.is_current ? 'Active now' : new Date(session.last_active).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {session.is_current ? (
                    <span className="text-xs font-medium text-emerald-600">Current</span>
                  ) : (
                    <button
                      onClick={() => revokeSession(session.id)}
                      className="text-xs font-medium text-red-600 hover:text-red-700"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )

  const renderNotificationsSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-6">Notification Preferences</h2>

        {/* Notification Channels */}
        <div className="space-y-4 mb-8">
          <div className="p-5 bg-white border border-slate-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Email Notifications</h3>
                  <p className="text-xs text-slate-500">Receive updates via email</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => updateSetting('emailNotifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
              </label>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">SMS Notifications</h3>
                  <p className="text-xs text-slate-500">Receive text messages for important updates</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.smsNotifications}
                  onChange={(e) => updateSetting('smsNotifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
              </label>
            </div>
          </div>

          <div className="p-5 bg-white border border-slate-200 rounded-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Push Notifications</h3>
                  <p className="text-xs text-slate-500">Receive push notifications on mobile app</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.pushNotifications}
                  onChange={(e) => updateSetting('pushNotifications', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Notification Types */}
        <div className="pt-6 border-t border-slate-200">
          <h3 className="text-sm font-semibold text-slate-900 mb-4">What to notify me about</h3>
          <div className="space-y-3">
            {[
              { key: 'transactionAlerts', label: 'Transaction Alerts', desc: 'Notifications for all transactions' },
              { key: 'securityAlerts', label: 'Security Alerts', desc: 'Important security-related notifications' },
              { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Promotions and product updates' }
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-slate-900 mb-0.5">{item.label}</p>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings[item.key]}
                    onChange={(e) => updateSetting(item.key, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-slate-900"></div>
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={saveNotifications}
            disabled={saving}
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Notification Settings
          </button>
        </div>
      </div>
    </div>
  )

  const renderPreferencesSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-6">Display & Regional</h2>

        <div className="space-y-6">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Language</label>
            <select
              value={settings.language}
              onChange={(e) => updateSetting('language', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            >
              <option value="en">English (US)</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="de">Deutsch</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Currency</label>
            <select
              value={settings.currency}
              onChange={(e) => updateSetting('currency', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            >
              <option value="USD">US Dollar (USD)</option>
              <option value="EUR">Euro (EUR)</option>
              <option value="GBP">British Pound (GBP)</option>
              <option value="JPY">Japanese Yen (JPY)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Timezone</label>
            <select
              value={settings.timezone}
              onChange={(e) => updateSetting('timezone', e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-900"
            >
              <option value="America/New_York">Eastern Time (ET)</option>
              <option value="America/Chicago">Central Time (CT)</option>
              <option value="America/Denver">Mountain Time (MT)</option>
              <option value="America/Los_Angeles">Pacific Time (PT)</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-700 mb-2">Theme</label>
            <div className="grid grid-cols-2 gap-4">
              {['light', 'dark'].map((theme) => (
                <button
                  key={theme}
                  onClick={() => updateSetting('theme', theme)}
                  className={`p-4 border-2 rounded-lg transition-all ${
                    settings.theme === theme
                      ? 'border-slate-900 bg-slate-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="text-sm font-medium text-slate-900 capitalize mb-1">{theme}</div>
                  <div className="text-xs text-slate-500">
                    {theme === 'light' ? 'Bright and clean' : 'Easy on the eyes'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={savePreferences}
            disabled={saving}
            className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg transition-all text-sm font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Preferences
          </button>
        </div>
      </div>
    </div>
  )

  const renderCardsSection = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-6">Cards & Payment Methods</h2>

        {cards.length === 0 ? (
          <div className="text-center py-12">
            <CreditCard className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-slate-900 mb-1">No Cards Found</h3>
            <p className="text-xs text-slate-500">You don't have any cards linked yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cards.map((card) => (
              <div key={card.id} className="p-5 bg-white border border-slate-200 rounded-xl">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      card.card_type === 'credit' ? 'bg-purple-50' : 'bg-blue-50'
                    }`}>
                      <CreditCard className={`w-5 h-5 ${
                        card.card_type === 'credit' ? 'text-purple-600' : 'text-blue-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900 mb-1">
                        {card.card_brand || 'Card'} •••• {card.last_four || '****'}
                      </h3>
                      <p className="text-xs text-slate-500">
                        Expires {card.expiry_month}/{card.expiry_year} • {card.card_type === 'credit' ? 'Credit' : 'Debit'}
                      </p>
                      <span className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded ${
                        card.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                        card.status === 'frozen' ? 'bg-blue-100 text-blue-700' :
                        'bg-slate-100 text-slate-700'
                      }`}>
                        {card.status?.charAt(0).toUpperCase() + card.status?.slice(1) || 'Active'}
                      </span>
                    </div>
                  </div>
                  <button className="text-sm font-medium text-slate-900 hover:text-slate-700">
                    Manage
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const renderSection = () => {
    switch (activeSection) {
      case 'profile':
        return renderProfileSection()
      case 'security':
        return renderSecuritySection()
      case 'notifications':
        return renderNotificationsSection()
      case 'preferences':
        return renderPreferencesSection()
      case 'cards':
        return renderCardsSection()
      case 'privacy':
        return (
          <div className="text-center py-12">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-sm font-semibold text-slate-900 mb-1">Privacy Settings</h3>
            <p className="text-xs text-slate-500">Control your data and privacy</p>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>
              <p className="text-sm text-slate-500 mt-0.5">Manage your account preferences</p>
            </div>
            <button
              onClick={fetchSettings}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>

          {/* Success Message */}
          {successMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2"
            >
              <Check className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-700">{successMessage}</span>
            </motion.div>
          )}

          {/* Error Message */}
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2"
            >
              <AlertCircle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">{error}</span>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-600 hover:text-red-800"
              >
                &times;
              </button>
            </motion.div>
          )}
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="space-y-2">
              {settingsSections.map((section, index) => {
                const IconComponent = section.icon
                return (
                  <motion.button
                    key={section.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full p-4 rounded-lg transition-all text-left ${
                      activeSection === section.id
                        ? 'bg-slate-900 text-white'
                        : 'bg-white border border-slate-200 hover:border-slate-900'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <IconComponent className={`w-5 h-5 ${
                        activeSection === section.id ? 'text-white' : 'text-slate-600'
                      }`} />
                      <div className="flex-1">
                        <div className={`text-sm font-semibold ${
                          activeSection === section.id ? 'text-white' : 'text-slate-900'
                        }`}>
                          {section.title}
                        </div>
                        <div className={`text-xs ${
                          activeSection === section.id ? 'text-white/70' : 'text-slate-500'
                        }`}>
                          {section.description}
                        </div>
                      </div>
                      {activeSection !== section.id && (
                        <ChevronRight className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </motion.button>
                )
              })}
            </div>

            {/* Account Actions */}
            <div className="mt-8 p-5 bg-red-50 border border-red-200 rounded-xl">
              <h3 className="text-sm font-semibold text-red-900 mb-2">Danger Zone</h3>
              <p className="text-xs text-red-700 mb-4">Permanent account actions</p>
              <button className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all text-sm font-medium">
                <Trash2 className="w-4 h-4" />
                Close Account
              </button>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              {renderSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
